import {
  useReactTable,
  ColumnDef,
  SortingState,
  getCoreRowModel,
  flexRender,
  PaginationState,
} from '@tanstack/react-table';
import { Dispatch, ReactNode, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LinkTrackingDto, TrackedLinkItemDto } from '@/Domain/api/Analytics/dto/LinkTracking.dto';
import { EmptyTableRow } from '@/Domain/components/Insights/LinkTracking/TableRow/EmptyTableRow';
import { ErrorTableRow } from '@/Domain/components/Insights/LinkTracking/TableRow/ErrorTableRow';
import { InactiveLinkTrackingTableRow } from '@/Domain/components/Insights/LinkTracking/TableRow/InactiveLinkTrackingTableRow';
import { LoadingTableRow } from '@/Domain/components/Insights/LinkTracking/TableRow/LoadingTableRow';
import { CardContent } from '@/Domain/components/Shared/Card/Card';
import { CenterSpinner } from '@/Domain/components/Shared/CenterSpinner/CenterSpinner';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationButton,
  PaginationNext,
  PaginationPrevious,
} from '@/Domain/components/Shared/Pagination/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/Domain/components/Shared/Table/Table';
import { SortAscIcon } from '@/Domain/components/icons/SortAsc.icon';
import { SortDescIcon } from '@/Domain/components/icons/SortDesc.icon';
import { PaginationHelper } from '@/Domain/helper/Pagination.helper';
import { cn } from '@/Domain/utils/cn';

type Props = {
  isError: boolean;
  isLoading: boolean;
  isPlaceholderData: boolean;
  isLinkTrackingActive: boolean;
  data: LinkTrackingDto | undefined;
  sorting: SortingState;
  pagination: PaginationState;
  setPagination: Dispatch<SetStateAction<PaginationState>>;
  setSorting: Dispatch<SetStateAction<SortingState>>;
};

export const LinkTrackingTable = ({
  isError,
  isLoading,
  isPlaceholderData,
  isLinkTrackingActive,
  data,
  sorting,
  setSorting,
  pagination,
  setPagination,
}: Props) => {
  const { t } = useTranslation('insights_page', { keyPrefix: 'ai_insights.link_tracking' });

  const totalPages = Math.ceil((data?.count ?? 0) / pagination.pageSize);
  const currentPage = pagination.pageIndex + 1;
  const showPagination = totalPages > 1 && !isError && !isLoading;

  const pages = useMemo(
    () => PaginationHelper.getRange(currentPage, totalPages),
    [currentPage, totalPages],
  );

  const columns: ColumnDef<TrackedLinkItemDto>[] = [
    {
      accessorKey: 'short_format_url',
      header: () => t('url'),
      enableSorting: false,
      meta: { fluid: true },
      cell: ({ row }) => {
        const shortUrl = row.original.short_format_url;
        const fullUrl = row.original.full_url;
        return fullUrl ? (
          <a
            href={fullUrl}
            target="_blank"
            rel="noreferrer"
            title={shortUrl}
            className="block truncate"
          >
            {shortUrl}
          </a>
        ) : (
          <span title={shortUrl} className="block truncate">
            {shortUrl}
          </span>
        );
      },
    },
    {
      accessorKey: 'generation_count',
      header: () => t('sent'),
      cell: (info) => info.getValue<number>(),
      enableSorting: true,
      meta: { align: 'right' },
    },
    {
      accessorKey: 'click_count',
      header: () => t('clicked'),
      cell: (info) => info.getValue<number>(),
      enableSorting: true,
      meta: { align: 'right' },
    },
    {
      id: 'conversion',
      header: () => t('conversion'),
      accessorFn: (row) =>
        row.generation_count ? (row.click_count / row.generation_count) * 100 : 0,
      cell: (info) => `${info.getValue<number>().toFixed(0)}%`,
      enableSorting: false,
      meta: { align: 'right' },
    },
  ];

  const table = useReactTable({
    data: data?.items || [],
    rowCount: data?.count || 0,
    columns,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    state: {
      sorting,
      pagination,
    },
  });

  const renderBody = (): ReactNode => {
    if (isError) return <ErrorTableRow />;
    if (isLoading) return <LoadingTableRow />;
    if (!isLinkTrackingActive && !data?.items.length) return <InactiveLinkTrackingTableRow />;
    if (!data?.items.length) return <EmptyTableRow />;

    return table.getRowModel().rows.map((row) => (
      <TableRow key={row.id}>
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={cell.id}
            className={cn(
              cell.column.columnDef.meta?.align === 'right'
                ? 'text-right font-medium whitespace-nowrap'
                : 'text-left',
              { 'w-full max-w-0': cell.column.columnDef.meta?.fluid },
            )}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  return (
    <CardContent className="pt-0">
      <div className="relative">
        <Table className="min-w-0" aria-busy={isPlaceholderData}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn('whitespace-nowrap', {
                      'cursor-pointer': header.column.columnDef.enableSorting,
                    })}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div
                      className={cn('group flex items-center gap-2', {
                        'justify-end': header.column.columnDef.meta?.align === 'right',
                      })}
                    >
                      <span
                        className={cn({
                          hidden: !header.column.columnDef.enableSorting,
                        })}
                      >
                        {{
                          asc: <SortAscIcon className="size-4.5" />,
                          desc: <SortDescIcon className="size-4.5" />,
                        }[header.column.getIsSorted() as string] || (
                          <SortDescIcon className="size-4.5 opacity-0 group-hover:!opacity-100 text-foreground-subtle ease-in-out transition-opacity duration-300" />
                        )}
                      </span>
                      <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody
            data-testid="link-tracking-table-body"
            className={cn('transition-opacity', { 'opacity-40': isPlaceholderData })}
          >
            {renderBody()}
          </TableBody>
        </Table>

        {isPlaceholderData && (
          <div
            data-testid="link-tracking-loading-overlay"
            className="absolute inset-0 flex items-center justify-center"
          >
            <CenterSpinner />
          </div>
        )}
      </div>

      {showPagination && (
        <div className="flex items-center justify-center pt-4 gap-3">
          <Pagination>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => table.previousPage()}
                disabled={isPlaceholderData || !table.getCanPreviousPage()}
              >
                {t('previous')}
              </PaginationPrevious>
            </PaginationItem>

            <PaginationContent>
              {pages.map((page, idx) =>
                page === '...' ? (
                  <PaginationEllipsis key={`ellipsis-${idx}`}>…</PaginationEllipsis>
                ) : (
                  <PaginationItem key={page}>
                    <PaginationButton
                      size="icon"
                      isActive={page === currentPage}
                      disabled={isPlaceholderData}
                      onClick={() => setPagination((old) => ({ ...old, pageIndex: page - 1 }))}
                    >
                      {page}
                    </PaginationButton>
                  </PaginationItem>
                ),
              )}
            </PaginationContent>

            <PaginationItem>
              <PaginationNext
                onClick={() => table.nextPage()}
                disabled={isPlaceholderData || !table.getCanNextPage()}
              >
                {t('next')}
              </PaginationNext>
            </PaginationItem>
          </Pagination>
        </div>
      )}
    </CardContent>
  );
};
